import React, { useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { FaBell, FaCheck, FaCheckDouble, FaExternalLinkAlt } from 'react-icons/fa';
import { socket } from '../App';
import { shouldUpdateNotificationBadge } from '../utils/notificationPrefs';

const ICON_MAP = {
    'notification_new_message': '💬',
    'nueva-venta': '💰',
    'nuevo-pedido-ecommerce': '🛒',
    'ecommerce_pregunta': '❓',
    'ecommerce_foto': '📸',
    'ecommerce_restock': '🔔',
    'ecommerce_stock_check': '📦',
    'meli': '🏪',
    'sistema': '🛠️',
    'info': 'ℹ️',
    'warning': '⚠️',
    'error': '❌',
    'success': '✅',
    'supervisor_venta_riesgo': '⏰',
    'seguimiento_vencido': '📅',
    'liquidacion_auto_desactivados': '🔄',
};

const timeAgo = (date) => {
    const now = new Date();
    const diff = now - new Date(date);
    const minutes = Math.floor(diff / 60000);
    if (minutes < 1) return 'Ahora';
    if (minutes < 60) return `${minutes}m`;
    const hours = Math.floor(minutes / 60);
    if (hours < 24) return `${hours}h`;
    const days = Math.floor(hours / 24);
    if (days < 7) return `${days}d`;
    return new Date(date).toLocaleDateString('es-CO', { day: '2-digit', month: 'short' });
};

const NotificationBell = () => {
    const [unreadCount, setUnreadCount] = useState(0);
    const [notifications, setNotifications] = useState([]);
    const [isOpen, setIsOpen] = useState(false);
    const [loading, setLoading] = useState(false);
    const [userPrefs, setUserPrefs] = useState(null);
    const dropdownRef = useRef(null);
    const navigate = useNavigate();

    const fetchUserPrefs = useCallback(async () => {
        try {
            const res = await axios.get('/api/notifications/preferences');
            setUserPrefs(res.data);
        } catch (err) {
            console.error('Error fetching notification preferences:', err);
        }
    }, []);

    // Fetch unread count
    const fetchUnreadCount = useCallback(async () => {
        try {
            const res = await axios.get('/api/notifications/unread-count');
            setUnreadCount(res.data.count);
        } catch (err) {
            console.error('Error fetching unread count:', err);
        }
    }, []);

    // Fetch recent notifications for dropdown
    const fetchNotifications = useCallback(async () => {
        setLoading(true);
        try {
            const res = await axios.get('/api/notifications', {
                params: { page: 1, limit: 10 },
            });
            setNotifications(res.data.notifications || []);
        } catch (err) {
            console.error('Error fetching notifications:', err);
        } finally {
            setLoading(false);
        }
    }, []);

    // Mark single notification as read
    const handleMarkRead = async (id, e) => {
        e?.stopPropagation();
        try {
            await axios.put(`/api/notifications/${id}/read`);
            setNotifications((prev) =>
                prev.map((n) => (n._id === id ? { ...n, isRead: true } : n))
            );
            setUnreadCount((prev) => Math.max(0, prev - 1));
        } catch (err) {
            console.error('Error marking as read:', err);
        }
    };

    // Mark all as read
    const handleMarkAllRead = async () => {
        try {
            await axios.put('/api/notifications/read-all');
            setNotifications((prev) => prev.map((n) => ({ ...n, isRead: true })));
            setUnreadCount(0);
        } catch (err) {
            console.error('Error marking all as read:', err);
        }
    };

    // Navigate to URL on click
    const handleNotificationClick = (notification) => {
        if (!notification.isRead) {
            handleMarkRead(notification._id);
        }
        if (notification.url) {
            navigate(notification.url);
            setIsOpen(false);
        }
    };

    // Toggle dropdown — opening the bell marks all as read (same as "Marcar todas")
    const toggleDropdown = () => {
        if (!isOpen) {
            const openAndLoad = async () => {
                if (unreadCount > 0) {
                    await handleMarkAllRead();
                }
                await fetchNotifications();
            };
            openAndLoad();
        }
        setIsOpen(!isOpen);
    };

    // Close on click outside
    useEffect(() => {
        const handleClickOutside = (e) => {
            if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
                setIsOpen(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    // User preferences (silent / badge-only / disabled types)
    useEffect(() => {
        fetchUserPrefs();
        const onPrefsUpdated = () => fetchUserPrefs();
        window.addEventListener('userPreferencesUpdated', onPrefsUpdated);
        return () => window.removeEventListener('userPreferencesUpdated', onPrefsUpdated);
    }, [fetchUserPrefs]);

    // Initial fetch + socket listener
    useEffect(() => {
        if (!userPrefs) return;

        fetchUnreadCount();

        const handleNewNotification = (data) => {
            if (!shouldUpdateNotificationBadge(userPrefs, data)) {
                return;
            }

            setUnreadCount((prev) => prev + 1);
            if (data._id) {
                setNotifications((prev) => [
                    { ...data, isRead: false, createdAt: data.timestamp || new Date().toISOString() },
                    ...prev.slice(0, 9),
                ]);
            }
        };

        socket.on('notification', handleNewNotification);
        return () => {
            socket.off('notification', handleNewNotification);
        };
    }, [fetchUnreadCount, userPrefs]);

    // Poll every 60s for unread count sync
    useEffect(() => {
        const interval = setInterval(fetchUnreadCount, 60000);
        return () => clearInterval(interval);
    }, [fetchUnreadCount]);

    return (
        <div className="relative" ref={dropdownRef}>
            {/* Bell Button */}
            <button
                onClick={toggleDropdown}
                className="relative flex items-center p-2 rounded-full hover:bg-white/10 transition-all ease-in-out cursor-pointer"
                title="Notificaciones"
            >
                <FaBell className="text-white text-xl hover:text-yellow-300 transition-colors" />
                {unreadCount > 0 && (
                    <span className="absolute -top-0.5 -right-0.5 bg-red-500 text-white text-[10px] font-bold rounded-full min-w-[18px] h-[18px] flex items-center justify-center px-1 border-2 border-indigo-600 shadow-md animate-pulse">
                        {unreadCount > 99 ? '99+' : unreadCount}
                    </span>
                )}
            </button>

            {/* Dropdown */}
            {isOpen && (
                <div className="fixed inset-x-4 top-[70px] md:absolute md:top-full md:right-0 md:inset-x-auto md:mt-2 md:w-[380px] max-h-[calc(100vh-100px)] md:max-h-[500px] bg-gray-900/95 backdrop-blur-md text-gray-100 shadow-2xl rounded-xl border border-gray-700 z-[9999] overflow-hidden animate-fade-in-down">
                    {/* Header */}
                    <div className="flex items-center justify-between px-4 py-3 border-b border-gray-700 bg-gray-800/50">
                        <h3 className="font-bold text-sm">Notificaciones</h3>
                        <div className="flex items-center gap-2">
                            {unreadCount > 0 && (
                                <button
                                    onClick={handleMarkAllRead}
                                    className="text-xs text-yellow-400 hover:text-yellow-300 transition-colors flex items-center gap-1 cursor-pointer"
                                >
                                    <FaCheckDouble className="text-[10px]" />
                                    Marcar todas
                                </button>
                            )}
                        </div>
                    </div>

                    {/* Notification List */}
                    <div className="overflow-y-auto max-h-[60vh] md:max-h-[380px] scrollbar-thin">
                        {loading && notifications.length === 0 ? (
                            <div className="flex items-center justify-center py-8 text-gray-500">
                                <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-yellow-500"></div>
                            </div>
                        ) : notifications.length === 0 ? (
                            <div className="flex flex-col items-center justify-center py-10 text-gray-500">
                                <FaBell className="text-3xl mb-2 opacity-30" />
                                <span className="text-sm">Sin notificaciones</span>
                            </div>
                        ) : (
                            notifications.map((n) => (
                                <div
                                    key={n._id}
                                    onClick={() => handleNotificationClick(n)}
                                    className={`flex items-start gap-3 px-4 py-3 border-b border-gray-800/50 cursor-pointer transition-all hover:bg-white/5 ${!n.isRead ? 'bg-yellow-500/5 border-l-2 border-l-yellow-500' : ''
                                        }`}
                                >
                                    {/* Icon */}
                                    <span className="text-xl mt-0.5 flex-shrink-0">
                                        {n.icon || ICON_MAP[n.type] || 'ℹ️'}
                                    </span>

                                    {/* Content */}
                                    <div className="flex-1 min-w-0">
                                        {n.title && (
                                            <p className="text-sm font-semibold text-white truncate">
                                                {n.title}
                                            </p>
                                        )}
                                        <p className="text-xs text-gray-400 line-clamp-2">
                                            {n.message}
                                        </p>
                                        <div className="flex items-center gap-2 mt-1">
                                            <span className="text-[10px] text-gray-600">
                                                {timeAgo(n.createdAt)}
                                            </span>
                                            {n.url && (
                                                <FaExternalLinkAlt className="text-[9px] text-gray-600" />
                                            )}
                                        </div>
                                    </div>

                                    {/* Read indicator */}
                                    {!n.isRead && (
                                        <button
                                            onClick={(e) => handleMarkRead(n._id, e)}
                                            className="flex-shrink-0 mt-1 p-1 rounded-full hover:bg-white/10 transition-colors"
                                            title="Marcar como leída"
                                        >
                                            <FaCheck className="text-[10px] text-yellow-500" />
                                        </button>
                                    )}
                                </div>
                            ))
                        )}
                    </div>

                    {/* Footer */}
                    <div className="border-t border-gray-700 bg-gray-800/50">
                        <button
                            onClick={() => {
                                navigate('/notificaciones');
                                setIsOpen(false);
                            }}
                            className="w-full py-2.5 text-center text-xs text-yellow-400 hover:text-yellow-300 hover:bg-white/5 transition-colors font-semibold cursor-pointer"
                        >
                            Ver todas las notificaciones
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
};

export default NotificationBell;
